import { action, observable } from 'mobx';
import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { BlueprintType, IBlueprintsDAO } from '../../../../../../api/blueprints/blueprints.dao';
import { ConnectionTemplateConnectionsField, IConnectionTemplateDTO, IConnectionTemplateField } from '../../../../../../api/blueprints/connection.template.types';
import { JobType } from '../../../../../../api/jobs.dao';
import { EventExecutionRuntime } from '../../../../../../domain/jobs/implementations/event-execution-runtime';
import { IBlueprintItemDetails } from '../../../../../../api/blueprints/blueprint-details.types';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../../../../infrastructure/utils/blueprint-details.mapper';
import { isBefore } from 'date-fns';
import { ConnectionVersion } from '../../../../../../constants/common.constants';

export type BlueprintFieldsValues = {
    [key in IConnectionTemplateField['name']]: IConnectionTemplateField['value'];
};

export class MeetingConnectionsTemplatesVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private blueprints: { [key: string]: IConnectionTemplateDTO } = {};
    @observable
    private selectedBlueprintId: string = '';
    @observable
    private loading: boolean = false;
    @observable
    public generatingConnections: boolean = false;
    @observable
    public blueprintConnectionsAmount: number = 0;
    @observable
    public isGenerationsCompleted: boolean = false;

    constructor(
        private readonly meetingApiParams: { eventId: number; accountId: number; meetingId: number },
        private readonly meetingEndTime: string,
        private readonly applyBlueprint: (fieldsValues: BlueprintFieldsValues) => void,
        private readonly onCompleteGenerateConnections: () => void,
        private readonly connectionVersion: ConnectionVersion,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly jobsPipeline = IoC.Container.get(EventExecutionRuntime),
    ) {

    }

    @mobx.action
    public async initialize(): Promise<void> {
        this.startLoading();
        try {
            const response = await this.blueprintsDAO.getConnectionTemplateList({ accountId: this.meetingApiParams.accountId });
            this.setBlueprints(response);
        } catch (error) {
            MeetingConnectionsTemplatesVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    @action
    private startLoading(): void {
        this.loading = true;
    }

    @action
    private stopLoading(): void {
        this.loading = false;
    }

    @action
    private startGeneratingConnections = (): void => {
        this.generatingConnections = true;
    };

    @action
    public stopGeneratingConnections = (): void => {
        this.generatingConnections = false;
    };

    public get blueprintId(): string | null {
        return this.selectedBlueprintId;
    }

    @mobx.action
    private setBlueprints(templates: IConnectionTemplateDTO[]): void {
        const _blueprints = {};

        templates
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(blueprint => {
                _blueprints[blueprint.blueprintId] = blueprint;
            })
            ;

        this.blueprints = _blueprints;
    }

    private getBlueprintFields(): IConnectionTemplateField[] {
        return this.blueprints[this.selectedBlueprintId]?.fields ?? [];
    }

    public getBlueprintFieldsValues(): BlueprintFieldsValues {
        return this.getBlueprintFields()
            .reduce((fieldsValues, { name, value }) => {
                fieldsValues[name] = value;
                return fieldsValues;
            }, {} as BlueprintFieldsValues)
            ;
    }

    @action
    public setBlueprintConnectionsAmount(): void {
        const connectionsField = this.getBlueprintFields().find(field => field.name === 'connections') as ConnectionTemplateConnectionsField;
        this.blueprintConnectionsAmount = connectionsField?.value?.length ?? 0;
    }

    public getBlueprintList(): IConnectionTemplateDTO[] {
        return Object.values(this.blueprints)
            .filter(blueprint => {
                const templateVersion = blueprint.connectionVersion ?? ConnectionVersion.V1;
                return templateVersion === this.connectionVersion;
            });
    }

    public getBlueprintsOptions(): { blueprintId: string; name: string }[] {
        return this.getBlueprintList()
            .map(({ blueprintId, name }) => ({ blueprintId: blueprintId, name: name }))
            ;
    }

    @mobx.action
    public selectBlueprint(blueprintId: string): void {
        this.selectedBlueprintId = blueprintId;
        this.setBlueprintConnectionsAmount();

        if (blueprintId) {
            this.applyBlueprint(this.getBlueprintFieldsValues());
        }
        this.setIsGenerationsCompleted(false);
    };


    @mobx.action
    public generateConnections = async (): Promise<void> => {
        this.startGeneratingConnections();
        try {
            const job = this.jobsPipeline.getJobByType(JobType.GenerateConnectionsBlueprintTrigger);
            job.onComplete(this.onCompleteGenerateConnections.bind(this));
            await job.trigger({
                blueprintId: this.blueprintId,
                meetingId: this.meetingApiParams.meetingId,
            });
        } catch (error) {
            MeetingConnectionsTemplatesVM.handleApiException(error);
            this.stopGeneratingConnections();
        }
    };

    public getSelectedBlueprint(): IConnectionTemplateDTO | null {
        return this.blueprints[this.selectedBlueprintId] ?? null;
    };

    public getSelectedBlueprintType(): BlueprintType {
        return this.getSelectedBlueprint()?.type;
    }

    public getSelectedBlueprintDetails(): IBlueprintItemDetails | null {
        const selectedBlueprint = this.getSelectedBlueprint();

        return selectedBlueprint
            ? convertBlueprintTemplateIntoBlueprintDetails(selectedBlueprint.type, selectedBlueprint)
            : null
            ;
    };

    public shouldDisableGenerateButton = (): boolean => {
        return !this.jobsPipeline.isReady() || this.isMeetingInThePast();
    };

    @mobx.action
    public setIsGenerationsCompleted = (value: boolean): void => {
        this.isGenerationsCompleted = value;
    };

    public shouldRenderGenerateSection = (): boolean => {
        return !!this.blueprintConnectionsAmount && !this.isGenerationsCompleted;
    };

    public isMeetingInThePast = (): boolean => {
        if (!this.meetingEndTime) return true;
        const now = new Date();
        const endDateTime = new Date(this.meetingEndTime);
        return isBefore(endDateTime, now);
    };
}
